import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { SHOWCASE_PROJECTS, CREATE_SHOWCASE_PROJECT, LIKE_SHOWCASE_PROJECT, DELETE_SHOWCASE_PROJECT } from '../graphql';
import { Star, Plus, Github, ExternalLink, X, Search, Heart, Trash2, Code } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const TECH_TAGS = ['React','Node.js','Python','ML/AI','Flutter','Android','iOS','Web3','DevOps','IoT','Unity','AR/VR','Data Science','Blockchain','Cloud'];

function ProjectCard({ project, onLike, onDelete, currentUserId }) {
  const [showComments, setShowComments] = useState(false);
  const isOwn = project.author?.id === currentUserId;

  return (
    <div className="card !p-5 hover:shadow-md transition-shadow group">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 bg-brand-50 rounded-full flex items-center justify-center text-brand-700 font-bold text-sm">
            {project.author?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-700">{project.author?.name}</p>
            <p className="text-xs text-gray-400">{project.author?.college}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOwn && (
            <button onClick={() => onDelete(project.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-opacity"><Trash2 size={14}/></button>
          )}
          <span className="text-xs text-gray-400">{new Date(project.createdAt).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>
        </div>
      </div>

      <h3 className="font-bold text-gray-900 mb-1">{project.title}</h3>
      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{project.description}</p>

      {/* Tech stack */}
      {project.techStack?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {project.techStack.map(t => (
            <span key={t} className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full font-medium">{t}</span>
          ))}
        </div>
      )}

      {/* Tags */}
      {project.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {project.tags.map(t => (
            <span key={t} className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full">#{t}</span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-3 border-t border-gray-50">
        <button onClick={() => onLike(project.id)} className={`flex items-center gap-1 text-sm font-medium transition-colors ${project.hasLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}>
          <Heart size={15} fill={project.hasLiked ? 'currentColor' : 'none'}/> {project.likes}
        </button>
        {project.githubUrl && (
          <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-700">
            <Github size={15}/> Code
          </a>
        )}
        {project.liveUrl && (
          <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-gray-400 hover:text-brand-600">
            <ExternalLink size={15}/> Live Demo
          </a>
        )}
        {project.college && <span className="text-xs text-gray-300 ml-auto">{project.college}</span>}
      </div>
    </div>
  );
}

export default function ShowcasePage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [techInput, setTechInput] = useState('');
  const [form, setForm] = useState({ title: '', description: '', techStack: [], githubUrl: '', liveUrl: '', tags: [], college: '' });

  const { data, refetch } = useQuery(SHOWCASE_PROJECTS, { variables: { search: search || undefined, tag: tag || undefined } });
  const [create, { loading: creating }] = useMutation(CREATE_SHOWCASE_PROJECT, { onCompleted: () => { refetch(); setShowCreate(false); }});
  const [like] = useMutation(LIKE_SHOWCASE_PROJECT, { onCompleted: () => refetch() });
  const [del] = useMutation(DELETE_SHOWCASE_PROJECT, { onCompleted: () => refetch() });

  const projects = data?.showcaseProjects || [];

  const addTech = (t) => { if (t && !form.techStack.includes(t)) setForm(f=>({...f,techStack:[...f.techStack,t]})); setTechInput(''); };

  const handleCreate = async (e) => {
    e.preventDefault();
    await create({ variables: { ...form, college: form.college || null, githubUrl: form.githubUrl || null, liveUrl: form.liveUrl || null } });
    setForm({ title: '', description: '', techStack: [], githubUrl: '', liveUrl: '', tags: [], college: '' });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-50 rounded-xl"><Code size={20} className="text-brand-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Showcase</h1>
            <p className="text-gray-500 text-sm">Share projects across universities · Like, explore, get inspired</p>
          </div>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={14}/> Share Project</button>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input className="input pl-8 w-full" placeholder="Search projects, descriptions…" value={search} onChange={e => { setSearch(e.target.value); setTimeout(() => refetch(), 400); }} />
        </div>
        <div className="flex flex-wrap gap-1">
          <button onClick={() => { setTag(''); refetch(); }} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${!tag ? 'bg-brand-600 text-white border-transparent' : 'border-gray-200 text-gray-500'}`}>All</button>
          {TECH_TAGS.map(t => (
            <button key={t} onClick={() => { setTag(tag === t ? '' : t); refetch({ tag: tag === t ? undefined : t }); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${tag===t ? 'bg-brand-600 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-brand-300'}`}>{t}</button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
        <span>{projects.length} projects shared</span>
        <span>·</span>
        <span>{projects.reduce((s,p) => s + p.likes, 0)} total likes</span>
      </div>

      {/* Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-16">
          <Code size={48} className="mx-auto mb-3 text-gray-300"/>
          <p className="text-gray-500">No projects yet.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">+ Share Project</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map(p => (
            <ProjectCard key={p.id} project={p} currentUserId={user?.id}
              onLike={id => like({ variables: { projectId: id } })}
              onDelete={id => del({ variables: { projectId: id } })} />
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">Share a Project</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-3">
              <input required className="input w-full" placeholder="Project title" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} />
              <textarea required className="input w-full resize-none h-20 text-sm" placeholder="What does it do? What problem does it solve?" value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} />
              <div className="grid grid-cols-2 gap-3">
                <input className="input w-full" placeholder="GitHub URL" value={form.githubUrl} onChange={e => setForm(f=>({...f,githubUrl:e.target.value}))} />
                <input className="input w-full" placeholder="Live demo URL" value={form.liveUrl} onChange={e => setForm(f=>({...f,liveUrl:e.target.value}))} />
              </div>
              {/* Tech stack */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Tech Stack</label>
                <div className="flex gap-2 mb-1.5">
                  <input className="input flex-1" placeholder="Add technology" value={techInput} onChange={e => setTechInput(e.target.value)} onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();addTech(techInput);}}} />
                  <button type="button" onClick={() => addTech(techInput)} className="btn-secondary text-sm">Add</button>
                </div>
                <div className="flex flex-wrap gap-1 mb-1">
                  {TECH_TAGS.map(t => <button key={t} type="button" onClick={() => addTech(t)} className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${form.techStack.includes(t) ? 'bg-brand-600 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-brand-300'}`}>{t}</button>)}
                </div>
                {form.techStack.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {form.techStack.map(t => <span key={t} onClick={() => setForm(f=>({...f,techStack:f.techStack.filter(x=>x!==t)}))} className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full cursor-pointer hover:bg-red-50 hover:text-red-500">{t} ×</span>)}
                  </div>
                )}
              </div>
              <input className="input w-full" placeholder="College/University (blank = visible to all)" value={form.college} onChange={e => setForm(f=>({...f,college:e.target.value}))} />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creating} className="btn-primary flex-1">{creating ? 'Sharing…' : 'Share Project'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
